import React from 'react'
import { Card, CardContent, Button, Grid } from '../../design-system'
import { Clock, ArrowLeft, ArrowRight, Flag } from 'lucide-react'

/**
 * Test Interface Component
 * Main interface for taking the IQ test
 */
const TestInterface = ({
  questions = [],
  currentQuestionIndex = 0,
  answers = {},
  timeLeft = 0,
  onAnswerSelect,
  onNextQuestion,
  onPreviousQuestion,
  onSkipQuestion,
  onSubmitTest,
  onJumpToQuestion,
  isLoading = false
}) => {
  const currentQuestion = questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === questions.length - 1
  const selectedAnswer = answers[currentQuestion?.id]

  // Combined handler for answer selection with auto-advance
  const handleAnswerSelect = (questionId, answerIndex) => {
    onAnswerSelect(questionId, answerIndex)

    // Auto-advance to next question after a short delay
    setTimeout(() => {
      if (!isLastQuestion) {
        onNextQuestion()
      }
    }, 500) // 500ms delay for better UX
  }

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  if (!currentQuestion) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading questions...</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header with timer and progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <span className="text-lg font-medium text-gray-700">
              {formatTime(timeLeft)}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {questions.length}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="mb-6">
            <div className="text-sm text-gray-500 mb-2">
              {currentQuestion.category}
            </div>
            <h2 className="text-xl font-semibold text-gray-900 leading-relaxed">
              {currentQuestion.question}
            </h2>
          </div>

          {/* Answer options */}
          <div className="space-y-3">
            {currentQuestion.options?.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                  selectedAnswer === index
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                disabled={isLoading}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedAnswer === index
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedAnswer === index && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-base">{option}</span>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onPreviousQuestion}
            disabled={currentQuestionIndex === 0 || isLoading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <Button
            variant="outline"
            onClick={onSkipQuestion}
            disabled={isLoading}
          >
            Skip
          </Button>
        </div>

        <div className="flex gap-3">
          {isLastQuestion ? (
            <Button
              onClick={onSubmitTest}
              disabled={isLoading}
              className="bg-success-500 hover:bg-success-600"
            >
              <Flag className="w-4 h-4 mr-2" />
              Submit Test
            </Button>
          ) : (
            <Button
              onClick={onNextQuestion}
              disabled={isLoading}
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Question navigation (for larger screens) */}
      <div className="mt-8 hidden md:block">
        <div className="text-sm text-gray-500 mb-3">Jump to question:</div>
        <div className="flex flex-wrap gap-2">
          {questions.map((_, index) => (
            <button
              key={index}
              onClick={() => onJumpToQuestion(index)}
              className={`w-10 h-10 rounded-lg border text-sm font-medium transition-all duration-200 ${
                index === currentQuestionIndex
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : answers[questions[index]?.id] !== undefined
                  ? 'border-success-500 bg-success-50 text-success-700'
                  : 'border-gray-300 hover:border-gray-400 text-gray-600'
              }`}
              disabled={isLoading}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TestInterface